import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { ItemTemplateEntity } from '@/types/api';

import { ParsedItemTemplate } from '../utils/item-template-csv-parser';

import { CreateItemTemplateDto } from './create-item-template';
import { getItemTemplateSchemaQueryKey } from './get-item-template-schema';
import { getItemTemplateSchemasQueryKey } from './get-item-template-schemas';
import { getItemTemplatesQueryKey } from './get-item-templates';

export interface ItemTemplateSuccess {
  success: true;
  itemTemplate: ItemTemplateEntity;
}

export interface ItemTemplateFailure {
  success: false;
  error: string;
}

export type ItemTemplateBatchCreateResponse = ItemTemplateSuccess[] | ItemTemplateFailure[];

export const batchCreateItemTemplates = async (
  orgId: string,
  projectId: string | undefined,
  itemTemplates: CreateItemTemplateDto[],
): Promise<ItemTemplateBatchCreateResponse> => {
  if (projectId) {
    const { data } = await api.post<
      ParsedItemTemplate[],
      ApiResponse<ItemTemplateBatchCreateResponse>
    >(`/orgs/${orgId}/projects/${projectId}/item-templates/batch`, itemTemplates);
    return data;
  }

  const { data } = await api.post<
    ParsedItemTemplate[],
    ApiResponse<ItemTemplateBatchCreateResponse>
  >(`/orgs/${orgId}/item-templates/batch`, itemTemplates);

  return data;
};

interface UseBatchCreateItemTemplatesOptions {
  mutationConfig?: MutationConfig<
    (itemTemplates: CreateItemTemplateDto[]) => Promise<ItemTemplateBatchCreateResponse>
  >;
}

export const useBatchCreateItemTemplates = ({
  mutationConfig,
}: UseBatchCreateItemTemplatesOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId } = useScopeIds('org');
  const { projectId } = useScopeIds();

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: getItemTemplatesQueryKey(orgId),
      });

      void queryClient.invalidateQueries({
        queryKey: getItemTemplateSchemasQueryKey(orgId),
      });

      void queryClient.invalidateQueries({
        queryKey: getItemTemplateSchemaQueryKey(orgId),
      });

      if (args[0].some((item) => !item.success)) {
        notifications.warning('csvImport.partialSuccess', {
          success: args[0].filter((item) => item.success).length,
          failed: args[0].filter((item) => !item.success).length,
        });
      } else {
        notifications.success('csvImport.success', { count: args[0].length });
      }

      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('csvImport.errors.importFailed');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (itemTemplates: CreateItemTemplateDto[]) =>
      batchCreateItemTemplates(orgId, projectId, itemTemplates),
  });
};
